import { computed, defineComponent, Fragment, h, onBeforeUnmount, ref, shallowRef, unref, watch } from 'vue';

import { classesStates, classNames, deepEqual, warn } from '@components/utils';

import { CdsButton, CdsFunctionalButton } from '../../../CdsButton';
import { CdsCheckbox } from '../../../CdsCheckbox';
import { CdsIcon } from '../../../CdsIcon';
import { CdsList, CdsListItem, CdsListGroup } from '../../../CdsList';
import { CdsMenu } from '../../../CdsMenu';
import { CdsDivider } from '../../../CdsDivider';
import { CdsRadio } from '../../../CdsRadioButton';
import { flattenKeys } from '../../composable';
import { useInjectSlots } from '../../context';

import FilterSearch from './FilterSearch';
import FilterWrapper from './FilterWrapper';

import type { NTree } from '../../../CdsTree/interface';
import type { NTable } from '../../interface';
import type { ComponentProps } from '@components/utils';
import type { VNode } from 'vue';

interface IFilterRestProps {
  confirm?: boolean;
  closeDropdown?: boolean;
}

interface IFilterDropdownProps<RecordType = NTable.TDefaultRecord> {
  column: NTable.NColumn.IColumn<RecordType>;
  filterState?: NTable.IFilterState<RecordType>;
  filterMultiple: boolean;
  filterMode?: 'menu' | 'tree';
  filterSearch?: NTable.TFilterSearch;
  columnKey: NTable.TKey;
  triggerFilter: (filterState: NTable.IFilterState<RecordType>) => void;
  locale: NTable.ILocale;
  getPopupContainer?: NTable.NCustomize.TGetPopupContainer;
}

export default defineComponent<IFilterDropdownProps>({
  name: 'FilterDropdown',
  props: [
    'column',
    'filterState',
    'filterMultiple',
    'filterMode',
    'filterSearch',
    'columnKey',
    'triggerFilter',
    'locale',
    'getPopupContainer'
  ] as any,
  setup(props, { slots }) {
    const contextSlots = useInjectSlots();
    const filterMode = computed(() => props.filterMode ?? 'menu');
    const filterSearch = computed(() => props.filterSearch ?? false);
    const filterDropdownVisible = computed(() => props.column.filterDropdownVisible);
    const visible = ref(false);
    const filtered = computed(
      () => !!(props.filterState && (props.filterState.filteredKeys?.length || props.filterState.forceFiltered))
    );
    const filterFlattenKeys = computed(() => flattenKeys(props.column?.filters));
    const filterDropdownRef = computed(() => {
      const { filterDropdown, customFilterDropdown } = props.column;

      return filterDropdown || (customFilterDropdown && unref(contextSlots).customFilterDropdown);
    });

    const filterIconRef = computed(() => {
      const { filterIcon } = props.column;
      return filterIcon || unref(contextSlots).customFilterIcon;
    });

    const triggerVisible = (newVisible: boolean) => {
      visible.value = newVisible;
      props.column.onFilterDropdownVisibleChange?.(newVisible);
    };

    const mergedVisible = computed(() =>
      typeof filterDropdownVisible.value === 'boolean' ? unref(filterDropdownVisible) : unref(visible)
    );

    const propFilteredKeys = computed(() => props.filterState?.filteredKeys);
    const filteredKeys = shallowRef<NTable.TKey[]>([]);

    const onSelectKeys = ({ selectedKeys }: { selectedKeys: NTable.TKey[] }) => {
      filteredKeys.value = selectedKeys;
    };

    const onCheck = (keys: NTable.TKey[], { node, checked }: { node: NTree.IEventDataNode; checked: boolean }) => {
      if (!props.filterMultiple) {
        onSelectKeys({ selectedKeys: checked && node.key ? [node.key] : [] });
      } else {
        onSelectKeys({ selectedKeys: keys });
      }
    };

    watch(
      propFilteredKeys,
      () => {
        if (!unref(visible)) return;
        onSelectKeys({ selectedKeys: unref(propFilteredKeys) || [] });
      },
      { immediate: true }
    );

    const openKeys = shallowRef<string[]>([]);
    const openRef = ref();

    const onOpenChange = (keys: string[]) => {
      openRef.value = setTimeout(() => {
        openKeys.value = keys;
      });
    };

    const onMenuClick = () => {
      clearTimeout(unref(openRef));
    };

    onBeforeUnmount(() => {
      clearTimeout(unref(openRef));
    });

    const searchValue = ref('');
    const onSearch: (...args: any[]) => void = (event) => {
      const { value } = event.target;
      searchValue.value = value;
    };

    // clear search value after close filter dropdown
    watch(visible, () => {
      if (!unref(visible)) {
        searchValue.value = '';
      }
    });

    //@todo remove this when will implement CdsTree component
    watch(
      filterMode,
      (value) => {
        if (value === 'tree') {
          warn('CdsTable', `'filterMode="tree" is not supported yet, use 'filterMode="menu" instead'`);
        }
      },
      { immediate: true }
    );

    // ======================= Submit ========================
    const internalTriggerFilter = (keys: NTable.TKey[] | undefined | null) => {
      const { column, columnKey, filterState } = props;
      const mergedKeys = keys && keys.length ? keys : null;

      if (mergedKeys === null && (!filterState || !filterState.filteredKeys)) return null;
      if (deepEqual(mergedKeys, filterState?.filteredKeys)) return null;

      props.triggerFilter({ column, key: columnKey, filteredKeys: mergedKeys });
    };

    const onConfirm = () => {
      triggerVisible(false);
      internalTriggerFilter(unref(filteredKeys));
    };

    const onReset = ({ confirm, closeDropdown }: IFilterRestProps = { confirm: false, closeDropdown: false }) => {
      if (confirm) {
        internalTriggerFilter([]);
      }

      if (closeDropdown) {
        triggerVisible(false);
      }

      searchValue.value = '';
      filteredKeys.value = [];
    };

    const doFilter = ({ closeDropdown }: IFilterRestProps = { closeDropdown: true }) => {
      if (closeDropdown) {
        triggerVisible(false);
      }
      internalTriggerFilter(unref(filteredKeys));
    };

    const onVisibleChange = (newVisible: boolean) => {
      if (newVisible && propFilteredKeys.value !== undefined) {
        // Sync filteredKeys on appear in controlled mode (propFilteredKeys.value !== undefined)
        filteredKeys.value = propFilteredKeys.value || [];
      }

      triggerVisible(newVisible);

      // Default will filter when closed
      if (!newVisible && !unref(filterDropdownRef)) {
        onConfirm();
      }
    };

    const onCheckAll = (event: Event) => {
      if ((event.target as HTMLInputElement).checked) {
        filteredKeys.value = unref(filterFlattenKeys) as NTable.TKey[];
      } else {
        filteredKeys.value = [];
      }
    };

    const getTreeData = ({ filters }: { filters?: NTable.NColumn.IFilterItem[] }) =>
      (filters || []).map((filter, index) => {
        const key = String(filter.value);
        const item: NTree.IDataNode = {
          title: filter.text,
          key: filter.value !== undefined ? key : index
        };

        if (filter.children) {
          item.children = getTreeData({ filters: filter.children });
        }

        return item;
      });

    const treeData = computed(() => getTreeData({ filters: props.column.filters }));
    // ======================= Submit ========================

    // ======================= Styles ========================
    const getFilterComponent = () => {
      const selectedKeys = filteredKeys.value;
      const { column, locale, getPopupContainer, filterMultiple } = props;

      if ((column.filters || []).length === 0) {
        return h(
          'div',
          {
            class: 'cds-table__dropdown-no-filters'
          },
          locale.filterEmptyText
        );
      }

      if (unref(filterMode) === 'tree') {
        return h(Fragment, [
          h(FilterSearch, {
            filterSearch: unref(filterSearch),
            value: unref(searchValue),
            onChange: onSearch,
            locale
          }),
          h(
            'div',
            { class: 'cds-table__filter-dropdown-tree' },
            filterMultiple
              ? h(CdsCheckbox, {
                  label: locale.filterCheckAll,
                  class: 'cds-table__filter-dropdown--checkall',
                  onChange: onCheckAll,
                  checked: selectedKeys.length === unref(filterFlattenKeys).length,
                  indeterminate: selectedKeys.length > 0 && selectedKeys.length < unref(filterFlattenKeys).length
                })
              : undefined
          ),
          h('div', {}, 'FilterMode `tree` not supported yet.') // @todo implement CdsTree component (FilterDropdown.tsx:374)
        ]);
      }

      return h(Fragment, [
        h(FilterSearch, {
          filterSearch: unref(filterSearch),
          value: unref(searchValue),
          onChange: onSearch,
          locale
        }),
        h(
          CdsList,
          {
            selected: selectedKeys,
            selectStrategy: filterMultiple ? 'independent' : 'single-independent',
            'onUpdate:selected': (value) => onSelectKeys({ selectedKeys: value as NTable.TKey[] }),
            'onUpdate:opened': (value) => onOpenChange(value as string[])
          },
          {
            default: () =>
              renderFilterItems({
                filters: column.filters || [],
                filterSearch: unref(filterSearch),
                filteredKeys: unref(filteredKeys),
                searchValue: unref(searchValue),
                filterMultiple
              })
          }
        )
      ]);
    };

    return () => {
      const { locale, column, getPopupContainer } = props;

      let dropdownContent: VNode | any;

      if (typeof filterDropdownRef.value === 'function') {
        dropdownContent = filterDropdownRef.value({
          setSelectedKeys: (selectedKeys: NTable.TKey[]) => onSelectKeys({ selectedKeys }),
          selectedKeys: unref(filteredKeys),
          confirm: doFilter,
          clearFilters: onReset,
          filters: column.filters,
          visible: unref(mergedVisible),
          column: column.__originColumn__
        });
      } else if (unref(filterDropdownRef)) {
        dropdownContent = unref(filterDropdownRef);
      } else {
        const selectedKeys = unref(filteredKeys) as any;
        dropdownContent = h(Fragment, [
          getFilterComponent(),
          h(CdsDivider),
          h('div', { class: 'cds-table__dropdown-buttons' }, [
            h(CdsButton, {
              text: locale.filterReset,
              appearance: 'secondary',
              disabled: selectedKeys.length === 0,
              size: 'sm',
              onClick: () => onReset()
            }),
            h(CdsButton, {
              text: locale.filterConfirm,
              appearance: 'primary',
              size: 'sm',
              onClick: onConfirm
            })
          ])
        ]);
      }

      const menu = h(FilterWrapper, { class: 'cds-table__dropdown' }, { default: () => dropdownContent });

      let filterIcon: VNode | any;
      if (typeof filterIconRef.value === 'function') {
        filterIcon = filterIconRef.value({
          filtered: unref(filtered),
          column: column.__originColumn__
        });
      } else if (unref(filterIconRef)) {
        filterIcon = unref(filterIconRef);
      } else {
        filterIcon = h(CdsIcon, { name: 'funnel' });
      }

      return h('div', { class: 'cds-table__filter-column' }, [
        h('span', { class: 'cds-table__column-title' }, slots.default?.()),
        h(
          CdsMenu,
          {
            modelValue: unref(visible),
            closeOnContentClick: false,
            transition: 'slide-transition-y',
            'onUpdate:modelValue': onVisibleChange
          },
          {
            activator: ({ props }: { props: ComponentProps<typeof CdsMenu> }) =>
              h(
                CdsFunctionalButton,
                {
                  icon: true,
                  tabindex: -1,
                  class: classNames('cds-table__filter-trigger', {
                    [classesStates.active]: unref(filtered)
                  }),
                  ...props
                },
                {
                  prepend: () => filterIcon
                }
              ),
            default: () => h('div', { class: 'cds-table__dropdown-content' }, menu)
          }
        )
      ]);
    };
  }
});

function renderFilterItems({
  filters,
  filteredKeys,
  filterMultiple,
  searchValue,
  filterSearch
}: {
  filters: NTable.NColumn.IFilterItem[];
  filteredKeys: NTable.TKey[];
  filterMultiple: boolean;
  searchValue: string;
  filterSearch: NTable.TFilterSearch;
}) {
  return filters.map((filter, index) => {
    const key = String(filter.value);

    if (filter.children) {
      return h(
        CdsListGroup,
        { key: key || index, class: 'cds-table__dropdown-submenu' },
        {
          activator: ({ props }: { props: ComponentProps<typeof CdsListItem> }) =>
            h(CdsListItem, { ...props }, { default: () => filter.text }),
          default: () =>
            renderFilterItems({
              filters: filter.children || [],
              filteredKeys,
              filterMultiple,
              searchValue,
              filterSearch
            })
        }
      );
    }

    const item = h(
      CdsListItem,
      {
        key: filter.value !== undefined ? key : index,
        value: filter.value !== undefined ? key : index,
        selected: filteredKeys.includes(key)
      },
      {
        prepend: ({ isSelected }: { isSelected: boolean }) =>
          filterMultiple
            ? h(CdsCheckbox, { modelValue: isSelected, hideMessages: true, tabindex: -1 })
            : h(
                'div',
                { class: 'cds-radio-button' },
                h(CdsRadio, {
                  label: 'dump',
                  checked: isSelected,
                  value: filter.value,
                  hideMessages: true,
                  tabindex: -1
                })
              ),
        default: () => h('span', filter.text as VNode)
      }
    );

    if (searchValue.trim()) {
      if (typeof filterSearch === 'function') {
        return filterSearch(searchValue, filter) ? item : undefined;
      }
      return searchValueMatched(searchValue, filter.text) ? item : undefined;
    }

    return item;
  });
}

function searchValueMatched(searchValue: string, text: any) {
  if (typeof text === 'string' || typeof text === 'number') {
    return text?.toString().toLowerCase().includes(searchValue.trim().toLowerCase());
  }
  return false;
}
